// ========================================
// TEAM MANAGEMENT COMPONENT - WITH BLOCKING/UNBLOCKING
// Enhanced with user management support
// ========================================

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import TeamDetailsModal from './TeamDetailsModal';
import TeamBudgetModal from './TeamBudgetModal';
import TeamCreateModal from './TeamCreateModal';
import MCPAddModal from './MCPAddModal';
import ConfirmModal from './shared/ConfirmModal';
import DeleteTeamConfirmModal from './shared/DeleteTeamConfirmModal';
import { unblockTeam, deleteTeams, getTeamSpend, getUserPreferences, saveUserPreferences } from '../services/api';
import { canDeleteTeams, isAdmin } from '../utils/permissions';

/**
 * TeamManagement Component
 *
 * Main component for managing teams and their configurations.
 * Features:
 * - Display all teams with budget, users, models
 * - Filter and search teams
 * - Block/Unblock teams
 * - Budget management
 * - User management (via TeamDetailsModal)
 *
 * @param {Object} data - Dashboard data containing teams, users, groups, models
 * @param {boolean} loading - Loading state
 * @param {Object} loadingStatus - Detailed loading status
 * @param {string} loadingMessage - Loading message to display
 * @param {Function} onRefresh - Callback to refresh dashboard data
 */
const TEAM_MANAGEMENT_PINNED_ORDER = ['BKK Users', 'CNX Users', 'Plus Users'];

const TeamManagement = ({
    data,
    loading,
    loadingStatus,
    loadingMessage,
    onRefresh
}) => {
    // ========================================
    // STATE MANAGEMENT
    // ========================================
    const { accounts } = useMsal();
    const { t } = useTranslation();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';
    const userCanDelete = canDeleteTeams(currentUserEmail);

    const [selectedTeam, setSelectedTeam] = useState(null);
    const [unblocking, setUnblocking] = useState(null);
    const [unblockConfirm, setUnblockConfirm] = useState({ isOpen: false, team: null });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showMCPModal, setShowMCPModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, team: null, userCount: 0 });
    const [deleting, setDeleting] = useState(null);

    // Group filter state — stores group IDs that are hidden
    const [hiddenGroupIds, setHiddenGroupIds] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const filterPanelRef = useRef(null);

    // Saved prefs store group names (stable across environments), not IDs
    const [hiddenGroupNames, setHiddenGroupNames] = useState(null); // null = not yet loaded

    // Load saved filter prefs on mount
    useEffect(() => {
        if (!currentUserEmail || currentUserEmail === 'unknown@aware.co.th') return;
        getUserPreferences(currentUserEmail).then(result => {
            if (result.success && Array.isArray(result.data?.hiddenGroupIds)) {
                setHiddenGroupNames(result.data.hiddenGroupIds);
            } else {
                setHiddenGroupNames(null);
            }
        });
    }, [currentUserEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    // Once groups AND prefs both available, resolve names → current IDs
    useEffect(() => {
        if (prefsLoaded) return;
        const groups = data.groups || [];
        if (groups.length === 0) return;
        if (!currentUserEmail || currentUserEmail === 'unknown@aware.co.th') return;
        if (hiddenGroupNames === null) return; // still waiting for prefs to load

        if (hiddenGroupNames.length > 0) {
            // Resolve saved names to current IDs
            const resolved = groups
                .filter(g => hiddenGroupNames.includes(g.name.trim()))
                .map(g => g.id)
                .filter(Boolean);
            setHiddenGroupIds(resolved);
        } else {
            // No saved prefs — apply default: hide non-pinned groups
            const defaultHiddenNames = groups
                .filter(g => !TEAM_MANAGEMENT_PINNED_ORDER.includes(g.name.trim()))
                .map(g => g.name.trim())
                .filter(Boolean);
            const defaultHiddenIds = groups
                .filter(g => !TEAM_MANAGEMENT_PINNED_ORDER.includes(g.name.trim()))
                .map(g => g.id)
                .filter(Boolean);
            setHiddenGroupIds(defaultHiddenIds);
            if (defaultHiddenNames.length > 0) {
                saveUserPreferences(currentUserEmail, { hiddenGroupIds: defaultHiddenNames });
            }
        }
        setPrefsLoaded(true);
    }, [data.groups, prefsLoaded, hiddenGroupNames, currentUserEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close filter panel on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
                setShowFilterPanel(false);
            }
        };
        if (showFilterPanel) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showFilterPanel]);

    const toggleGroupVisibility = (groupId) => {
        const groups = data.groups || [];
        setHiddenGroupIds(prev => {
            const next = prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId];
            const nextNames = groups
                .filter(g => next.includes(g.id))
                .map(g => g.name.trim());
            saveUserPreferences(currentUserEmail, { hiddenGroupIds: nextNames });
            return next;
        });
    };

    // Team spend state - stores calculated spend from logs for each team
    const [teamSpendData, setTeamSpendData] = useState({});
    // teamSpendData structure: { [teamId]: { spend: number, loading: boolean, error: string } }


    // ========================================
    // FETCH TEAM SPEND DATA
    // ========================================

    /**
     * Fetch calculated spend from logs for a team
     * @param {Object} team - Team object with budget info
     */
    const fetchTeamSpendFromLogs = async (team) => {
        const teamId = team.team_id;

        // Skip if already loading or loaded
        if (teamSpendData[teamId]?.loading) return;

        // Calculate budget period dates
        const { start, end, periodEnd } = getBudgetPeriod(team.budget_duration, team.budget_reset_at);

        // Set loading state
        setTeamSpendData(prev => ({
            ...prev,
            [teamId]: { spend: 0, loading: true, error: null, start, end, periodEnd }
        }));

        try {
            // Pass list of all known team IDs to backend for health check detection
            const knownTeamIds = data.teams?.map(t => t.team_id).filter(Boolean) || [];
            const result = await getTeamSpend(teamId, start, end, knownTeamIds);

            if (result.success && result.data) {
                // Use total_spend from API if available (includes records without end_user)
                // Otherwise fall back to summing user spend
                const totalSpend = result.total_spend !== undefined
                    ? result.total_spend
                    : result.data.reduce((sum, user) => sum + (user.spend || 0), 0);

                setTeamSpendData(prev => ({
                    ...prev,
                    [teamId]: { spend: totalSpend, loading: false, error: null, start, end, periodEnd }
                }));
            } else {
                throw new Error(result.error || 'Failed to fetch spend');
            }
        } catch (error) {
            console.error(`Error fetching spend for team ${teamId}:`, error);
            setTeamSpendData(prev => ({
                ...prev,
                [teamId]: { spend: 0, loading: false, error: error.message, start, end, periodEnd }
            }));
        }
    };

    // Fetch spend data for all group-matched teams on mount and when data changes
    useEffect(() => {
        if (!data.teams || data.teams.length === 0) return;
        if (!data.groups || data.groups.length === 0) return;

        const groupNames = new Set(data.groups.map(g => g.name.trim().toLowerCase()));
        const groupTeams = data.teams.filter(
            t => t.team_alias && groupNames.has(t.team_alias.trim().toLowerCase())
        );

        groupTeams.forEach(team => {
            fetchTeamSpendFromLogs(team);
        });
    }, [data.teams, data.groups]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========================================
    // LOADING STATES
    // ========================================
    if (loading || loadingStatus?.overall === 'loading') {
        return (
            <div className="card">
                <h2>{t('teamManagement.title')}</h2>
                <div className="loading">{loadingMessage || t('teamManagement.loading')}</div>
            </div>
        );
    }

    if (!data.groups || data.groups.length === 0) {
        return (
            <div className="card">
                <h2>{t('teamManagement.title')}</h2>
                <div className="loading">{t('teamManagement.noTeams')}</div>
            </div>
        );
    }

    // ========================================
    // DATA EXTRACTION
    // ========================================
    const teams = data.teams || [];
    const users = data.users || [];
    const groups = data.groups || [];
    const models = data.models || [];

    // BKK/CNX/Plus always first, remaining groups alphabetically after
    const PINNED_ORDER = TEAM_MANAGEMENT_PINNED_ORDER;

    const allGroupRows = [...groups]
        .sort((a, b) => {
            const ai = PINNED_ORDER.indexOf(a.name.trim());
            const bi = PINNED_ORDER.indexOf(b.name.trim());
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return a.name.localeCompare(b.name);
        })
        .map(group => {
            const team = teams.find(
                t => t.team_alias && t.team_alias.trim().toLowerCase() === group.name.trim().toLowerCase()
            ) || null;
            return { group, team };
        });

    const groupRows = allGroupRows.filter(({ group }) => !hiddenGroupIds.includes(group.id));
    const hiddenCount = allGroupRows.length - groupRows.length;

    // ========================================
    // UTILITY FUNCTIONS
    // ========================================

    /**
     * Calculate budget period start and end dates for a team
     * Handles different budget durations: daily (24h/1d), monthly (30d), yearly (365d)
     *
     * @param {string} budgetDuration - Budget duration (e.g., "24h", "1d", "30d", "365d")
     * @param {string} budgetResetAt - ISO timestamp when budget resets (not currently used)
     * @returns {{ start: string, end: string, periodEnd: string }} Date range in YYYY-MM-DD format
     *           start: first day of budget period
     *           end: today (for querying)
     *           periodEnd: last day of budget period (for display)
     */
    const getBudgetPeriod = (budgetDuration = '30d', budgetResetAt = null) => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];

        // Normalize budget duration to lowercase for comparison
        const duration = (budgetDuration || '30d').toLowerCase();

        // Daily budget (24h or 1d) - show only today's spend
        if (duration === '24h' || duration === '1d') {
            return {
                start: today,
                end: today,
                periodEnd: today
            };
        }

        // Yearly budget (365d) - show current calendar year
        if (duration === '365d') {
            const year = now.getUTCFullYear();
            const firstDayOfYear = new Date(Date.UTC(year, 0, 1));
            const lastDayOfYear = new Date(Date.UTC(year, 11, 31));

            return {
                start: firstDayOfYear.toISOString().split('T')[0],
                end: today,
                periodEnd: lastDayOfYear.toISOString().split('T')[0]
            };
        }

        // Monthly budget (30d) or default - show current calendar month
        const year = now.getUTCFullYear();
        const month = now.getUTCMonth();
        const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
        const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));

        return {
            start: firstDayOfMonth.toISOString().split('T')[0],
            end: today,
            periodEnd: lastDayOfMonth.toISOString().split('T')[0]
        };
    };


    /**
     * Format budget duration for display
     * @param {string} duration - Duration string from API
     * @returns {string} Formatted duration string
     */
    const formatCost = (amount) => {
        if (amount === 0) return '$0.00';
        if (amount < 0.01) return `$${amount.toPrecision(3)}`;
        if (amount < 1) return `$${amount.toFixed(4)}`;
        return `$${amount.toFixed(2)}`;
    };

    const formatDuration = (duration) => {
        if (!duration) return t('common.noData');
        if (duration === '1d') return t('teamManagement.daily');
        if (duration === '7d') return t('teamManagement.weekly');
        if (duration === '30d') return t('teamManagement.monthly');
        if (duration === '365d') return t('teamManagement.yearly');
        if (duration === '24h') return t('teamManagement.daily');
        return duration;
    };

    // ========================================
    // ACTION HANDLERS
    // ========================================

    /**
     * Show confirmation for unblock team action
     * @param {Object} team - Team object to unblock
     */
    const handleUnblock = (team) => {
        setUnblockConfirm({ isOpen: true, team });
    };

    /**
     * Execute unblock team action
     * Unblocks a team and logs the action in audit trail
     */
    const executeUnblock = async () => {
        const team = unblockConfirm.team;
        if (!team) return;

        setUnblocking(team.team_id);

        const result = await unblockTeam(
            team.team_id,
            currentUserEmail,
            team.team_alias
        );

        setUnblockConfirm({ isOpen: false, team: null });

        if (result.success) {
            // Refresh team data
            if (onRefresh) {
                await onRefresh();
            }
        } else {
            alert(`Failed to unblock team: ${result.error}`);
        }

        setUnblocking(null);
    };

    /**
     * Handle team details update callback
     * Refreshes dashboard data after user add/remove operations
     */
    const handleTeamDetailsUpdate = async () => {
        if (onRefresh) {
            await onRefresh();
        }
    };

    /**
     * Show confirmation for delete team action
     * @param {Object} team - Team object to delete
     */
    const handleDelete = (team) => {
        const matchingGroup = groups.find(
            g => g.name.trim().toLowerCase() === (team.team_alias || '').trim().toLowerCase()
        );
        const userCount = Array.isArray(matchingGroup?.user_ids) ? matchingGroup.user_ids.length : 0;
        setDeleteConfirm({ isOpen: true, team, userCount });
    };

    /**
     * Execute delete team action
     * Deletes team from both LiteLLM and OpenWebUI
     */
    const executeDelete = async () => {
        const team = deleteConfirm.team;
        if (!team) return;

        setDeleting(team.team_id);

        const result = await deleteTeams(
            [team.team_id],
            [team.team_alias],
            currentUserEmail
        );

        setDeleteConfirm({ isOpen: false, team: null, userCount: 0 });

        if (result.success) {
            if (onRefresh) {
                await onRefresh();
            }
        } else {
            alert(`Failed to delete team: ${result.error}`);
        }

        setDeleting(null);
    };

    // ========================================
    // COMPONENT RENDER
    // ========================================
    return (
        <div className="card">
            {/* Card header */}
            <div className="card-header">
                <h2 className="card-title">
                    {t('teamManagement.title')}
                    <span className="card-title-count">{groupRows.length}/{allGroupRows.length}</span>
                    {hiddenCount > 0 && (
                        <span className="team-hidden-badge">{t('teamManagement.hiddenCount', { count: hiddenCount })}</span>
                    )}
                </h2>
                <div className="team-filters" ref={filterPanelRef}>
                    <button
                        onClick={() => setShowFilterPanel(prev => !prev)}
                        className="btn btn-secondary"
                        title={t('teamManagement.filterTeams')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="12" y1="18" x2="12" y2="18" />
                        </svg>
                        {t('teamManagement.filter')}
                        {hiddenCount > 0 && <span className="team-filter-badge">{hiddenCount}</span>}
                    </button>

                    {showFilterPanel && (
                        <div className="team-filter-panel">
                            <div className="team-filter-panel-label">{t('teamManagement.showHideTeams')}</div>
                            {allGroupRows.map(({ group }) => (
                                <label key={group.id} className="team-filter-row">
                                    <input
                                        type="checkbox"
                                        checked={!hiddenGroupIds.includes(group.id)}
                                        onChange={() => toggleGroupVisibility(group.id)}
                                    />
                                    {group.name}
                                </label>
                            ))}
                            {hiddenCount > 0 && (
                                <button
                                    className="team-filter-show-all"
                                    onClick={() => {
                                        setHiddenGroupIds([]);
                                        saveUserPreferences(currentUserEmail, { hiddenGroupIds: [] }); // empty names = show all
                                    }}
                                >
                                    {t('teamManagement.showAll')}
                                </button>
                            )}
                        </div>
                    )}

                    {isAdmin(currentUserEmail) && (
                        <button
                            onClick={() => setShowMCPModal(true)}
                            className="btn btn-secondary"
                        >
                            Add MCP
                        </button>
                    )}
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-primary"
                    >
                        {t('teamManagement.createGroup')}
                    </button>
                </div>
            </div>

            {/* Groups table */}
            <div className="team-table-wrap">
                <div className="team-table-head">
                    <span>{t('teamManagement.team')}</span>
                    <span>{t('teamManagement.users')}</span>
                    <span>{t('teamManagement.budgetUsed')}</span>
                    <span>{t('teamManagement.status')}</span>
                    <span></span>
                </div>
                {groupRows.map(({ group, team }, index) => {
                    const rowName = group.name;
                    const userCount = Array.isArray(group.user_ids) ? group.user_ids.length : 0;
                    const modelCount = team?.models?.length || 0;
                    const duration = team ? formatDuration(team.budget_duration) : '';

                    const spendData = team ? teamSpendData[team.team_id] : null;
                    const currentSpend = spendData?.spend || 0;
                    const isLoadingSpend = spendData?.loading || false;
                    const spendError = spendData?.error;

                    const maxBudget = team?.max_budget || 0;
                    const rawPercent = maxBudget > 0 ? Math.min(100, (currentSpend / maxBudget) * 100) : 0;
                    const usagePercent = rawPercent > 0 && rawPercent < 1 ? parseFloat(rawPercent.toFixed(2)) : Math.round(rawPercent);
                    const isHighUsage = usagePercent >= 90;
                    const isMediumUsage = usagePercent >= 75 && usagePercent < 90;
                    const isBlocked = team?.blocked || false;
                    const hasExceededBudget = maxBudget > 0 && currentSpend >= maxBudget;

                    const barClass = isHighUsage ? 'danger' : isMediumUsage ? 'warning' : 'ok';

                    const statusPill = !team
                        ? <span className="team-status-pill">{t('teamManagement.noEngineTeam') || 'No engine team'}</span>
                        : isBlocked
                            ? <span className="team-status-pill blocked">Blocked</span>
                            : hasExceededBudget
                                ? <span className="team-status-pill over-budget">Over budget</span>
                                : isHighUsage
                                    ? <span className="team-status-pill high-usage">High usage</span>
                                    : <span className="team-status-pill active">Active</span>;

                    return (
                        <div
                            key={group.id || index}
                            className={`team-table-row ${isBlocked ? 'team-table-row--blocked' : ''}`}
                        >
                            {/* Group name + meta */}
                            <div className="team-table-name">
                                <div className="team-table-name-primary">{rowName}</div>
                                {team && (
                                    <div className="team-table-name-meta">
                                        {modelCount} {modelCount !== 1 ? t('teamManagement.models') : t('teamManagement.model')} &middot; {duration}
                                    </div>
                                )}
                            </div>

                            {/* User count */}
                            <div className="team-table-users">{userCount}</div>

                            {/* Budget bar */}
                            <div className="team-table-budget">
                                {!team ? (
                                    <span className="team-budget-none">—</span>
                                ) : maxBudget > 0 ? (
                                    <>
                                        <div className="team-budget-bar-wrap">
                                            <div
                                                className={`team-budget-bar team-budget-bar--${barClass}`}
                                                style={{ width: `${usagePercent}%` }}
                                            />
                                        </div>
                                        <div className="team-budget-labels">
                                            {isLoadingSpend ? (
                                                <span className="team-budget-loading">Loading...</span>
                                            ) : spendError ? (
                                                <span className="team-budget-error" title={spendError}>Error</span>
                                            ) : (
                                                <span className={`team-budget-spend ${isHighUsage ? 'high' : ''}`}>
                                                    {formatCost(currentSpend)}
                                                </span>
                                            )}
                                            <span className="team-budget-max">/ {formatCost(maxBudget)}</span>
                                            <span className="team-budget-pct">{usagePercent}%</span>
                                        </div>
                                        {spendData?.start && spendData?.periodEnd && (
                                            <div className="team-budget-period">
                                                {new Date(spendData.start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – {new Date(spendData.periodEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="team-budget-none">{t('teamManagement.noBudgetLimit')}</span>
                                )}
                            </div>

                            {/* Status pill */}
                            <div className="team-table-status">{statusPill}</div>

                            {/* Actions */}
                            <div className="team-table-actions">
                                {team && (
                                    <button
                                        onClick={() => setSelectedTeam({ team, action: 'view' })}
                                        className="btn btn-secondary"
                                    >
                                        {t('teamManagement.viewDetails')}
                                    </button>
                                )}
                                {team && isBlocked && (
                                    <button
                                        onClick={() => handleUnblock(team)}
                                        className="btn btn-primary"
                                        disabled={unblocking === team.team_id}
                                    >
                                        {unblocking === team.team_id ? t('teamManagement.unblocking') : t('teamManagement.unblockTeam')}
                                    </button>
                                )}
                                {team && userCanDelete && (
                                    <button
                                        onClick={() => handleDelete(team)}
                                        className="btn btn-danger"
                                        disabled={deleting === team.team_id}
                                    >
                                        {deleting === team.team_id ? t('teamManagement.deleting') : t('teamManagement.delete')}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* View Details Modal */}
            {selectedTeam && selectedTeam.action === 'view' && (
                <TeamDetailsModal
                    team={selectedTeam.team}
                    allUsers={users}
                    allGroups={groups}
                    allModels={models}
                    onClose={() => setSelectedTeam(null)}
                    onEditQuota={() =>
                        setSelectedTeam({
                            team: selectedTeam.team,
                            action: 'edit'
                        })
                    }
                    onUpdate={handleTeamDetailsUpdate}
                />
            )}

            {/* Edit Budget Modal */}
            {selectedTeam && selectedTeam.action === 'edit' && (
                <TeamBudgetModal
                    team={selectedTeam.team}
                    onClose={() => setSelectedTeam(null)}
                    onUpdate={handleTeamDetailsUpdate}
                    allUsers={users}
                />
            )}

            {/* Unblock Confirmation Modal */}
            <ConfirmModal
                isOpen={unblockConfirm.isOpen}
                onClose={() => setUnblockConfirm({ isOpen: false, team: null })}
                onConfirm={executeUnblock}
                title={t('teamManagement.unblockTeam')}
                message={t('teamManagement.unblockMessage', { team: unblockConfirm.team?.team_alias })}
                details={t('teamManagement.unblockDetails')}
                variant="warning"
                confirmLabel={t('teamManagement.unblockTeam')}
                loading={unblocking !== null}
            />

            {/* Delete Confirmation Modal - Enhanced with name typing requirement */}
            <DeleteTeamConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, team: null, userCount: 0 })}
                onConfirm={executeDelete}
                teamName={deleteConfirm.team?.team_alias || ''}
                userCount={deleteConfirm.userCount || 0}
                loading={deleting !== null}
            />

            {/* Create Group modal — creates both OpenWebUI group and LiteLLM team together */}
            {showCreateModal && (
                <TeamCreateModal
                    onClose={() => setShowCreateModal(false)}
                    onUpdate={handleTeamDetailsUpdate}
                    allModels={models}
                    allUsers={users}
                />
            )}

            {/* Add MCP Server modal — admin only */}
            {showMCPModal && (
                <MCPAddModal
                    onClose={() => setShowMCPModal(false)}
                    onAdded={() => { setShowMCPModal(false); if (onRefresh) onRefresh(); }}
                    userEmail={currentUserEmail}
                />
            )}

        </div>
    );
};

TeamManagement.propTypes = {
    data: PropTypes.shape({
        teams: PropTypes.array,
        users: PropTypes.array,
        groups: PropTypes.array,
        models: PropTypes.array
    }),
    loading: PropTypes.bool,
    loadingStatus: PropTypes.object,
    loadingMessage: PropTypes.string,
    onRefresh: PropTypes.func
};

TeamManagement.defaultProps = {
    data: { teams: [], users: [], groups: [], models: [] },
    loading: false,
    loadingStatus: null,
    loadingMessage: '',
    onRefresh: null
};

export default TeamManagement;
